/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import dayjs from 'dayjs';

export type AgendaView = 'list' | 'day' | '3day' | 'week' | 'month';

/**
 * The three consecutive dates of a 3-day day-grid view, starting at `anchor`.
 * @param anchor - The first day of the 3-day window.
 * @returns Three dates at midnight, `anchor` through `anchor + 2 days`.
 */
export const threeDayDates = (anchor: Date): Date[] => {
  const start = dayjs(anchor).startOf('day');
  return Array.from({ length: 3 }, (_, i) => start.add(i, 'day').toDate());
};

/** A calendar grid cell (used by month and mini-calendar). */
export type MonthCell = {
  date: Date;
  isCurrentMonth: boolean;
};

/**
 * Format a date as an `YYYY-MM-DD` key.
 * @param d - The date to format.
 * @returns The ISO date string (no time component).
 */
export const dateKey = (d: Date): string => dayjs(d).format('YYYY-MM-DD');

/**
 * The seven dates (Mon..Sun) of the week containing `anchor`.
 * @param anchor - Any date within the target week.
 * @returns Seven dates at midnight, Monday through Sunday.
 */
export const weekDates = (anchor: Date): Date[] => {
  const day = dayjs(anchor).day();
  const offsetToMonday = day === 0 ? -6 : 1 - day;
  const monday = dayjs(anchor).add(offsetToMonday, 'day').startOf('day');
  return Array.from({ length: 7 }, (_, i) => monday.add(i, 'day').toDate());
};

/**
 * Month grid with only the weeks needed to display the month.
 * Returns 4, 5 or 6 weeks — never a trailing row that is entirely
 * from the next month.
 * @param year - The full year of the target month.
 * @param month - The zero-based month index (0 = January).
 * @returns The month cells, week-aligned, with a current-month flag each.
 */
export const monthGrid = (year: number, month: number): MonthCell[] => {
  const first = dayjs(new Date(year, month, 1));
  const startOffset = first.day();
  const gridStart = first.subtract(startOffset, 'day');
  const cells: MonthCell[] = [];
  let i = 0;
  while (true) {
    const weekCells = Array.from({ length: 7 }, (_, d) => {
      const date = gridStart.add(i + d, 'day');
      return { date: date.toDate(), isCurrentMonth: date.month() === month };
    });
    const weekHasCurrentMonth = weekCells.some((c) => c.isCurrentMonth);
    if (!weekHasCurrentMonth) break;
    cells.push(...weekCells);
    i += 7;
  }
  return cells;
};

/**
 * Visible UTC range for a view anchored at `anchor`.
 * @param view - The active agenda view (day, 3day, week, month).
 * @param anchor - The anchor date the view is centered on.
 * @returns The inclusive start/end datetimes covering the visible cells.
 */
export const viewRange = (
  view: AgendaView,
  anchor: Date
): { start: Date; end: Date } => {
  if (view === 'list') {
    return {
      start: dayjs(anchor).startOf('day').toDate(),
      end: dayjs(anchor).add(6, 'day').endOf('day').toDate(),
    };
  }
  /*
   * The day view fetches its whole week, not just its day.
   *
   * The mobile day screen carries a week strip that marks which days have
   * something on them, and it cannot mark what was never fetched: scoped to
   * one day, every dot but the selected one was blank, which reads as an
   * empty week rather than as missing data. Widening the range also makes
   * stepping through the strip instant, since the neighbouring days are
   * already in hand. Both day views filter by `isSameDay` before rendering,
   * so the extra occurrences cost a slightly larger response and change
   * nothing on screen.
   */
  if (view === 'day') {
    const dates = weekDates(anchor);
    return {
      start: dayjs(dates[0]).startOf('day').toDate(),
      end: dayjs(dates[6]).endOf('day').toDate(),
    };
  }
  if (view === '3day') {
    const dates = threeDayDates(anchor);
    return {
      start: dayjs(dates[0]).startOf('day').toDate(),
      end: dayjs(dates[2]).endOf('day').toDate(),
    };
  }
  if (view === 'week') {
    const dates = weekDates(anchor);
    return {
      start: dayjs(dates[0]).startOf('day').toDate(),
      end: dayjs(dates[6]).endOf('day').toDate(),
    };
  }
  const grid = monthGrid(anchor.getFullYear(), anchor.getMonth());
  return {
    start: dayjs(grid[0].date).startOf('day').toDate(),
    end: dayjs(grid[grid.length - 1].date).endOf('day').toDate(),
  };
};

/**
 * Move the anchor date forward/backward by one unit of the given view.
 * @param view - The active agenda view determining the step unit.
 * @param anchor - The current anchor date.
 * @param direction - `1` to advance, `-1` to go back.
 * @returns The shifted anchor date.
 */
export const shiftAnchor = (
  view: AgendaView,
  anchor: Date,
  direction: 1 | -1
): Date => {
  if (view === 'list') {
    return dayjs(anchor).add(direction * 7, 'day').toDate();
  }
  if (view === '3day') {
    return dayjs(anchor).add(direction * 3, 'day').toDate();
  }
  const unit = view === 'day' ? 'day' : view === 'week' ? 'week' : 'month';
  return dayjs(anchor).add(direction, unit).toDate();
};

/**
 * Minutes since midnight (local) for an ISO timestamp.
 * @param iso - The ISO timestamp to convert.
 * @returns The count of minutes elapsed since local midnight.
 */
export const minutesOfDay = (iso: string): number => {
  const d = dayjs(iso);
  return d.hour() * 60 + d.minute();
};

/**
 * Whether an ISO timestamp falls on the same calendar day as `day`.
 * @param iso - The ISO timestamp to test.
 * @param day - The reference calendar day.
 * @returns True when both fall on the same local day.
 */
export const isSameDay = (iso: string, day: Date): boolean =>
  dayjs(iso).isSame(dayjs(day), 'day');
